import { Component, EventEmitter, OnInit, Output, ViewChild, AfterViewInit } from "@angular/core";
import { Subject } from "rxjs";
import { DefaultValuesService } from "../../services/defaultvalues.service";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { CreateKpiProductHierarchyPopupComponent } from "./kpiproducthierarchy-popup/kpiproducthierarchy-popup.component";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { KpiProductHierarchyService } from "./kpiproducthierarchy-master.service";
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
    selector: "kpiproducthierarchy-master",
    standalone: true,
    imports: [
        MatTableModule,
        MatDialogModule,
        MatSortModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatTooltipModule,
        MatProgressBarModule,
        ReactiveFormsModule,
        FormsModule
    ],
    templateUrl: './kpiproducthierarchy-master.component.html',
    styleUrls: [
        '../../style/mat-table.component.less',
        '../../style/dialogue-box.less',
        '../../style/tables/table-add-butt-header-orange.less',
        '../../style/new-generic-styles.component.less',
        'kpiproducthierarchy-master.component.less'
    ]
})

export class KpiProductHierarchyMasterComponent implements OnInit, AfterViewInit {
    @Output() reloadProducts = new EventEmitter<void>();
    @ViewChild(MatSort) sort!: MatSort;

    allProducts: KpiProductHierarchy[] = [];
    originalProducts: KpiProductHierarchy[] = [];
    modifiedProducts: KpiProductHierarchy[] = [];
    allLinesOfBusiness: KpiBreakdown[] = [];
    allAssetTypes: KpiBreakdown[] = [];
    currentFilterValue: string = '';
    timeoutFunction: any;
    public productsForm!: FormGroup;
    public dataSource = new MatTableDataSource<any>();
    isCallSucceed: number = 0;

    protected _onDestroy = new Subject<void>();
    columnsToDisplay = ['LineOfBusiness', 'AssetType', 'KpiProductHierarchyId', 'ProductHierarchyDescription', 'actions'];

    constructor(private readonly defaults: DefaultValuesService, 
        private readonly dialogForm: MatDialog, 
        private readonly kpiProductHierarchyService: KpiProductHierarchyService,
        private readonly toastrService: ToastrService,
        private readonly fb: FormBuilder) {}

    ngOnInit() {
        this.getHeader();
        this.productsForm = this.fb.group({
            productRows: this.fb.array([])
        });
        this.getLinesOfBusiness();
        this.getAssetTypes();
        this.getProducts();
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    getHeader() {
        if (this.defaults.isReadonlyUser)
            this.columnsToDisplay = ['LineOfBusiness', 'AssetType', 'KpiProductHierarchyId', 'ProductHierarchyDescription'];
        else
            this.columnsToDisplay = ['LineOfBusiness', 'AssetType', 'KpiProductHierarchyId', 'ProductHierarchyDescription', 'actions'];
    }

    get productRows(): FormArray {
        return this.productsForm.get('productRows') as FormArray;
    }

    getProducts() {
        this.kpiProductHierarchyService.getAllKpiProductsHierarchy().subscribe({
            next: (data: KpiProductHierarchy[]) => {
                this.originalProducts = structuredClone(data);
                this.allProducts = data;
                this.mapDataToForm(data);
            }   
        });
    }

    mapDataToForm(data: KpiProductHierarchy[]) {
        this.productsForm = this.fb.group({
            productRows: this.fb.array(data.map((val, index) => this.fb.group({
                LineOfBusinessBreakdownId: new FormControl(val.LineOfBusinessBreakdownId),
                LineOfBusiness: new FormControl(val.LineOfBusiness),
                AssetTypeBreakdownId: new FormControl(val.AssetTypeBreakdownId),
                AssetType: new FormControl(val.AssetType),
                KpiProductHierarchyId: new FormControl(val.KpiProductHierarchyId),
                ProductHierarchyDescription: new FormControl(val.ProductHierarchyDescription, [Validators.required]),
                isEditable: new FormControl(true),
                isNewRow: new FormControl(false)
            })))
        });
        const rows = (this.productsForm.get('productRows') as FormArray).value.map((row: any, index: number) => ({
            ...row,
            originalIndex: index
        }));

        // Default sort: alphabetically by Asset Type, then Product hierarchy ID,
        // then Product Name (Global).
        rows.sort((a: any, b: any) =>
            (a.AssetType ?? '').localeCompare(b.AssetType ?? '')
            || (a.KpiProductHierarchyId ?? '').localeCompare(b.KpiProductHierarchyId ?? '')
            || (a.ProductHierarchyDescription ?? '').localeCompare(b.ProductHierarchyDescription ?? '')
        );

        this.dataSource.data = rows;
    }

    getLinesOfBusiness() {
        this.kpiProductHierarchyService.getLinesOfBusinessKpiProductHierarchy().subscribe({
            next: (data: KpiBreakdown[]) => {
                this.allLinesOfBusiness = data;
            }
        });
    }

    getAssetTypes() {
        this.kpiProductHierarchyService.getAssetTypesKpiProductHierarchy().subscribe({
            next: (data: KpiBreakdown[]) => {
                this.allAssetTypes = data;
            }
        });
    }

    filter($event: any) {
        if (this.timeoutFunction) {
            clearTimeout(this.timeoutFunction);
            this.timeoutFunction = null;
        }
        this.timeoutFunction = setTimeout(() => {
            this.currentFilterValue = $event.target.value;
            this.applyFilter(this.currentFilterValue);
        }, 300);
    }

    applyFilter(filterValue: string) {
        const filter = filterValue.trim().toLowerCase();
        const allRows = (this.productsForm.get('productRows') as FormArray).value;
        
        if (filter) {
            const filteredData = allRows
                .map((row, index) => ({ ...row, originalIndex: index }))
                .filter((x: any) =>
                    x.LineOfBusiness?.toLowerCase().indexOf(filter) > -1
                    || x.AssetType?.toLowerCase().indexOf(filter) > -1
                    || x.ProductHierarchyDescription?.toLowerCase().indexOf(filter) > -1
                    || x.KpiProductHierarchyId?.toLowerCase().indexOf(filter) > -1
                );
            this.dataSource.data = filteredData;
        } else {
            this.dataSource.data = allRows.map((row, index) => ({ ...row, originalIndex: index }));
        }
    }

    onGetHistory(element: any) {
        this.dialogForm.open(CreateKpiProductHierarchyPopupComponent, {
            panelClass: ["product-hierarchy-dialogue", "custom-mat-dialog_v2"],
            width: '1000px',
            maxWidth: '90vw',
            data: { action: "history", productSelected: element.KpiProductHierarchyId }
        });
    }
}